import React, { FC } from 'react';
import { useSelector } from 'react-redux';
import { createFragmentContainer, graphql } from 'react-relay';
import { TRADE, CONSUMER } from 'dibs-buyer-layout/exports/userTypeEnums';
import { ProductTileAd } from '../../components/global/Ads/ProductTileAd';
import { TradeRewardsMessagingWrapper } from '../../components/global/TradeRewardsMessagingWrapper';
import { getProductTileAdMap } from '../../utils/adHelpers';

import { SbSharedProductAdWrapper_itemSearch } from './__generated__/SbSharedProductAdWrapper_itemSearch.graphql';
import { SbSharedProductAdWrapper_user } from './__generated__/SbSharedProductAdWrapper_user.graphql';
import { SbSharedProductAdWrapper_viewer } from './__generated__/SbSharedProductAdWrapper_viewer.graphql';

type Props = {
    index: number;
    pageCol?: number;
    itemSearch: SbSharedProductAdWrapper_itemSearch;
    user: SbSharedProductAdWrapper_user;
    viewer: SbSharedProductAdWrapper_viewer;
};

// TODO: replace with State type
type ReduxState = {
    relayVariables: {
        variables: {
            first: number;
            isTrade: boolean;
        };
    };
};

export const SbSharedProductAdWrapperComponent: FC<Props> = ({
    index,
    pageCol,
    itemSearch,
    user,
    viewer,
}) => {
    const relayVariables = useSelector((state: ReduxState) => state.relayVariables.variables);
    const isTradeAdsEnabled = !viewer?.isTradeDoubleClickAdsOnSbDisabled;
    // reading isTrade value from relay vars b/c we need to get correct ad config values
    // during first render when google tag script starts loading ads.
    const showTradeAds = isTradeAdsEnabled && relayVariables.isTrade;

    const adMap = getProductTileAdMap({
        uriRef: itemSearch?.displayUriRef,
        pageCol,
        pageSize: relayVariables.first,
        userType: showTradeAds ? TRADE : CONSUMER,
    } as $TSFixMe); // TODO: remove once converted to TS

    const ad = adMap[index + 1]; // 1-indexed

    if (!ad) {
        return null;
    }

    if (user?.tradeFirm?.serviceId && !isTradeAdsEnabled) {
        return <TradeRewardsMessagingWrapper user={user} />;
    }

    const { adId, elementId } = ad;
    return <ProductTileAd adId={adId} elementId={elementId} />;
};

export const SbSharedProductAdWrapper = createFragmentContainer(SbSharedProductAdWrapperComponent, {
    viewer: graphql`
        fragment SbSharedProductAdWrapper_viewer on Viewer {
            isTradeDoubleClickAdsOnSbDisabled: isEnabled(key: "disableTradeDoubleClickAdsOnSB")
        }
    `,
    itemSearch: graphql`
        fragment SbSharedProductAdWrapper_itemSearch on ItemSearchQueryConnection {
            displayUriRef
        }
    `,
    user: graphql`
        fragment SbSharedProductAdWrapper_user on User {
            tradeFirm @include(if: $isTrade) {
                serviceId
            }
            ...TradeRewardsMessagingWrapper_user
        }
    `,
});
